import { ref, computed, watch } from "vue"
import { debounce, DEBOUNCE_DELAY } from "@/helpers/debounce.js"
import { getBucketSize, getBucketFreq } from "@/helpers/filter.js"
import useNotificationStore from "@/stores/notificationStore.js"
import { PORTFOLIO } from "@/stores/repower/meteringPointStoreFactories.js"

// Chart series keys that categorizeRecord aggregates; other chart keys are ignored.
const CHART_SERIES_KEYS = [
  "allocation",
  "revenue",
  "positionDayAhead",
  "nomination",
]
// Mark the last N days a provisional regardless of record stability
export const FORCE_PROVISIONAL = 5 // days

/**
 * Shared data-loading and record-categorization logic for the results panels.
 *
 * @param {() => Object|null} meteringPointGetter - Getter for the metering
 * point.
 * @param {() => Date[]} dateRangeGetter - Getter for the selected date range.
 * @param {Object} [options]
 * @param {boolean} [options.loadOptimaBenefit=false] - Also fetch optima
 * benefit.
 */
export default function useResultsData(
  meteringPointGetter,
  dateRangeGetter,
  { loadOptimaBenefit = false } = {},
) {
  const notificationStore = useNotificationStore()
  const recordsData = ref()
  const optimaBenefit = ref()
  const hasMissingMeasurementData = ref(false)
  const loading = ref(false)
  const loadingFailed = ref(false)

  const bucketSize = computed(() => getBucketSize(dateRangeGetter()))

  /**
   * Groups record types by chart series, including whether they should be added
   * or subtracted. This is a condensed mapping of the more sparsely defined
   * 'chart_spec' values of each record type.
   */
  const recordTypeChartMap = computed(() => {
    if (!recordsData.value) {
      return {}
    }
    const map = {}
    for (const [recordType, { chart_specs }] of Object.entries(
      recordsData.value.types,
    )) {
      for (const chartSpec of chart_specs) {
        if (chartSpec?.sign) {
          map[chartSpec.key] = map[chartSpec.key] ?? {}
          map[chartSpec.key][recordType] = chartSpec.sign
        }
      }
    }
    return map
  })

  /**
   * Indicates whether the 'nomination' recordTypeChartMap category has a
   * different composition than the 'positionDayAhead' category.
   */
  const shouldSplitNomination = computed(() => {
    const orderedStringify = (obj) =>
      JSON.stringify(obj, Object.keys(obj).sort())
    return (
      orderedStringify(recordTypeChartMap.value.nomination ?? {}) !==
      orderedStringify(recordTypeChartMap.value.positionDayAhead ?? {})
    )
  })

  /**
   * Sums the signed record type values for a single chart series, or returns
   * null if none of its record types are present.
   */
  function sumSeries(record, signs, shouldInclude) {
    let value = null
    for (const [type, sign] of Object.entries(signs)) {
      if (shouldInclude(type)) {
        value = (value ?? 0) + sign * record[type].value
      }
    }
    return value
  }

  /**
   * Calculate the grouped sum of all values in a record. Record types are
   * grouped and summed according to the chart series key and 'sign' in the
   * recordTypeChartMap.
   *
   * Chart keys that are not in CHART_SERIES_KEYS are ignored.
   *
   * @param {Object} record - The record to categorize
   * @param {Function} [options.typeFilter] - Optional predicate (spec) =>
   * boolean to exclude specific record types from aggregation.
   */
  function categorizeRecord(record, { typeFilter = null } = {}) {
    const shouldInclude = (type) =>
      type in record &&
      (!typeFilter || typeFilter(recordsData.value.types[type]))

    const result = {}
    for (const serie of CHART_SERIES_KEYS) {
      const signs = recordTypeChartMap.value[serie]
      const value = signs ? sumSeries(record, signs, shouldInclude) : null
      if (value !== null) {
        result[serie] = value
      }
    }
    return result
  }

  function onNewRecords(newRecords) {
    recordsData.value = newRecords
    loading.value = false
  }

  function onLoadRecordsFailed(error) {
    loadingFailed.value = true
    loading.value = false
    notificationStore.pushError(
      "Fout bij het ophalen van resultaten",
      `De resultaten konden niet worden opgehaald. Probeer het later opnieuw. (code: ${error?.code})`,
      "load-records-error",
    )
  }

  function onNewOptimaBenefit(data) {
    optimaBenefit.value = data.optima_benefit
    hasMissingMeasurementData.value = !!data.has_missing_measurement_data
  }

  function onLoadOptimaBenefitFailed(error) {
    notificationStore.pushError(
      "Fout bij het ophalen van Optima voordeel",
      `Het optima voordeel kon niet worden opgehaald. Probeer het later opnieuw. (code: ${error.code})`,
      "load-optima-benefit-error",
    )
  }

  /**
   * Fetches the results data from the Repower API. Debounced to prevent
   * excessive calls while clicking through the UI.
   */
  const loadData = debounce((meteringPoint, dateRange) => {
    meteringPoint.loadResults(
      getBucketFreq(bucketSize.value),
      dateRange,
      false,
      onNewRecords,
      onLoadRecordsFailed,
    )
    if (loadOptimaBenefit && meteringPoint.role !== PORTFOLIO) {
      meteringPoint.loadOptimaBenefit(
        dateRange,
        onNewOptimaBenefit,
        onLoadOptimaBenefitFailed,
      )
    }
  }, DEBOUNCE_DELAY)

  /**
   * Resets and (re)loads the results data when the metering point or date range
   * changes.
   */
  watch(
    [meteringPointGetter, dateRangeGetter],
    ([meteringPoint, dateRange]) => {
      recordsData.value = null
      optimaBenefit.value = null
      hasMissingMeasurementData.value = false
      if (!meteringPoint) {
        return
      }
      loading.value = true
      loadingFailed.value = false
      loadData(meteringPoint, dateRange)
    },
    { immediate: true },
  )

  return {
    recordsData,
    optimaBenefit,
    hasMissingMeasurementData,
    loading,
    loadingFailed,
    bucketSize,
    recordTypeChartMap,
    shouldSplitNomination,
    categorizeRecord,
  }
}
